import {
  BaseModel,
  BaseModelArray,
  BaseModelPagedArray,
  BaseModelPagedRequest
} from '@eview/core/base/base-model';
import { User } from '@eview/core/models/user';
import { Form } from '../form/form';

export interface Post extends BaseModel {
  id: number;
  user: User;
  parent_id: number;
  parent: { id: number };
  form: Form;
  message: string;
  color: string;
  type: PostType;
  title: string;
  slug: string;
  content: string;
  /** Análisis realizado del informe. */
  analysis?: string | null;
  /** Archivo propio del análisis (media.id). */
  analysis_media_id?: number | null;
  author_contact?: string;
  author_email: string;
  author_realname: string;
  status: PostStatus;
  /** Estado del conflicto (emergente|activo|escalado|en_negociacion|resuelto|reactivado). */
  conflict_status?: ConflictStatus | string | null;
  created: Date;
  updated: Date;
  locale: string;
  values: any;
  post_date: Date;
  tags: any[];
  published_to: any[];
  completed_stages: any[];
  sets: any[];
  linked_posts: number;
  lock: PostLock;
  source: PostSource;
  contact: any;
  data_source_message_id: number;

  priority: PostPriority;
  mgmt_lev_1: string;
  mgmt_lev_2: string;
  mgmt_lev_3: string;
  mgmt_lev: PostManagementLevel;
  icon: string;
  isEditing?: boolean;
  published?: boolean;
  /** Sustento obligatorio para retirar la publicación. */
  unpublish_reason?: string | null;
  proceso?: string;
  institucion?: string;
  /** Legacy shared detail (kept for older rows). */
  status_detail?: string;
  /** Detalle documento/medio de derivación (estado Derivado). */
  status_detail_derivado?: string;
  /** Detalle documento/medio de cierre (estado Cerrado). */
  status_detail_cerrado?: string;
}

export interface Posts extends BaseModelPagedArray<Post> { }

export interface PostLock extends BaseModel {
  user: User;
  post_id: number;
  expires: number;
}

export interface PostLocks extends BaseModelArray<PostLock> { }

export enum PostType {
  Incident = 'incident',
  Risk = 'risk',
  // Lern = 'mace'
}

export enum PostStatus {
  All = 'all',
  Pending = 'draft',
  InProgressVerification = 'verification',
  Verified = 'published',
  Unverified = 'archived',
  Responded = 'responded',
  Evaluated = 'evaluated',
  /** Estados ONPE/MACE */
  Recibido = 'recibido',
  Monitoreado = 'monitoreado',
  Analizado = 'analizado',
  Derivado = 'derivado',
  Cerrado = 'cerrado',
}

/** Legacy incident status workflow (forms.status_scheme = default). */
export const DEFAULT_POST_STATUSES: PostStatus[] = [
  PostStatus.Pending,
  PostStatus.InProgressVerification,
  PostStatus.Unverified,
  PostStatus.Verified,
  PostStatus.Responded,
  PostStatus.Evaluated
];

/** ONPE/MACE ordered workflow (forms.status_scheme = onpe_mace). */
export const ONPE_POST_STATUSES: PostStatus[] = [
  PostStatus.Recibido,
  PostStatus.Monitoreado,
  PostStatus.Analizado,
  PostStatus.Derivado,
  PostStatus.Cerrado
];

/** Estado del conflicto (informe raíz y actualizaciones). Distinto del estado ONPE `cerrado`. */
export enum ConflictStatus {
  Emergente = 'emergente',
  Activo = 'activo',
  Escalado = 'escalado',
  EnNegociacion = 'en_negociacion',
  Resuelto = 'resuelto',
  Reactivado = 'reactivado'
}

export const CONFLICT_STATUSES: ConflictStatus[] = [
  ConflictStatus.Emergente,
  ConflictStatus.Activo,
  ConflictStatus.Escalado,
  ConflictStatus.EnNegociacion,
  ConflictStatus.Resuelto,
  ConflictStatus.Reactivado
];

export enum PostSource {
  Web = 'web',
  SMS = 'sms',
  Twitter = 'twitter',
  Email = 'email'
}

export enum PostPriority {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  Critical = 'critical',
  // Valores antiguos para compatibilidad
  Standard = 'standard',
  Urgent = 'urgent'
}

export enum PostManagementLevel {
  Unknown = 0,
  Subregional = 1,
  Regional = 2,
  National = 3
}

export enum PostFilters {
  Incident_Status = 'incident_status',
  Priority = 'priority',
  Department = 'mgmt_lev_3',
  Province = 'mgmt_lev_1',
  District = 'mgmt_lev_2',
}

export enum FormFilters {
  Target = 'Target',
  Perpetrators = 'Alleged perpetrators',
  Second_Perpetrators = 'Second alleged perpetrators',
  Status = 'Status',
  Means = 'Means',
  Source_of_information = "Source of information",
  Are_Womem_the_main_target = "Are women the main target",
  Election_related = "Election related",
  Politically_motivated = "Politically Motivated",
  Are_Youth_the_main_target = "Are youth the main target",
  Are_Person_Disability_the_main_target = "Are persons with disabilities or the elderly at risk/main target",
  Incident_venue = "Incident venue"
}

export enum RiskFilters {
  CREDIBILITY_RISK = "Credibility of the risk",
  RISK_TYPE = "Risk type",
  TYPE_OF_LOCATION = "Types of location",
  LIKELIHOOD_RISK = "Likelihood of the risk",
  IMPACT_RISK = "Impact of the risk",
  STATGE_RISK = "Stage of electoral process risk applies to",
  PROVINCE = 'Province',
  DISTRICT = 'District',
  WARD = 'Ward',
  DEPARTMENT= 'department'
}

export interface ListPostOptions extends BaseModelPagedRequest {
  status: PostStatus;
  form?: any;
  has_location?: string;
  source?: PostSource;
  tags?: any;
  parent?: any;
  post_id?: number;
  created_before?: Date;
  created_after?: Date;
  parent_id?: number;
  formId?: number;
  type?: string;
  limit?: number;
  multiple_tags?: any;
  q?: string;
  /** Columna `posts.published` (p. ej. rail del mapa). */
  published?: boolean;
}

export interface FilterOptions {
  mgmt_level: string;
  start_date: Date;
  end_date: Date;
  category: string;
}

export interface FilterItem {
  label: string,
  queryParam: string,
  values: any[],
  type?: string,
  input?: string
}

export enum PostMediaType {
  Photo = 'image',
  Video = 'video'
}

export enum CategoriesEnum {
  categories = 'type-of-incident',
  categories_List = "conflictividad-categoría"
}

export enum LegendTypeEnum {
  categories = 'categories',
  status = 'status'
}

export interface PostCommentPayload {
  post_id: number,
  message: string
}

export interface GetCommentPayload {
  post_id: number
}

export interface Comments {
  id: number,
  message: string,
  post_id: number,
  status: string,
  created: any,
  author: string
}
